/**
 * Station sponsorships and per-station ad insertion (issue #51).
 *
 * Centova Cast station provisioning is off-limits for this codebase (see
 * PROJECTS.md), so ad insertion happens client-side: the player mixes a
 * station's sponsor sweeper into the stream audio via the same Web Audio
 * ducking pipeline that plays Kali's DJ drops (useKaliBroadcast). Each
 * sponsored station carries at most one sweeper, played once per tune-in.
 *
 * Adding a sponsor: add an entry to STATION_SPONSORSHIPS keyed by the
 * station id from Home.tsx, generate the sweeper MP3 with
 * scripts/generate-sponsor-sweepers.py (lands in client/public/audio/sponsors/),
 * and redeploy. Stations without an entry are unsponsored — no sweepers,
 * no sponsor line in the player bar.
 */

export interface StationSponsorship {
  /** Sponsor display name, e.g. in the player bar ("Sponsored by …"). */
  name: string;
  /** Click-through URL, or null when the sponsor has no public site. */
  url: string | null;
  /**
   * Sweeper MP3 (public path) inserted into this station's stream once per
   * tune-in, or null when the sponsorship is display-only.
   */
  audio: string | null;
  /** The spoken sweeper line — kept with the data so copy stays reviewable. */
  script: string;
}

export const STATION_SPONSORSHIPS: Record<string, StationSponsorship> = {
  eclectic: {
    name: "AYCE Pickleball",
    url: "https://aycepickleball.com",
    audio: "/audio/sponsors/ayce-pickleball-sweeper.mp3",
    script:
      "The Pickle Jar is brought to you by AYCE Pickleball — all you can eat pickleball. Grab your gear at aycepickleball dot com!",
  },
  "wind-down": {
    name: "DD Hypnosis Recordings",
    url: null, // production platform — no public domain yet
    // Retired 2026-09-09: the old 8s sweeper is replaced by the kali +
    // Dominick duo drop (kali-winddown-songbyuniverse.mp3) which plays
    // every 3-4 songs on Wind Down. audio:null = no sweeper plays.
    audio: null,
    script:
      "Wind Down is sponsored by D D Hypnosis Recordings — deep relaxation for your mind and body. Breathe out, and enjoy the calm.",
  },
};

/** Look up the sponsorship for a station id; null when unsponsored. */
export function sponsorshipForStation(
  stationId: string | null | undefined
): StationSponsorship | null {
  if (!stationId) return null;
  return STATION_SPONSORSHIPS[stationId] ?? null;
}
